import type {Component, ComponentModelDefinition, ComponentView, ComponentViewDefinition, StyleProps} from 'grapesjs';
import {DIVIDER_DEFAULT_HEIGHT, EMAIL_MAKER_COMMANDS, EMAIL_MAKER_COMPONENT_TYPES, EMAIL_MAKER_PROPS, RTE_TAGNAMES} from '@EmailMakerReact/email-maker-react.constants';
import type {ComponentRegistrationContext} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';
import {defaultViewEvents, matchByTagAndAttr} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/helpers/component-helpers';
import {setCountdownDefaults, refreshCountdown} from '@EmailMakerReact/lib/email-maker-countdown-library';
import {
  COUNTDOWN_ATTRIBUTES,
  COUNTDOWN_DEFAULT_FONT_FAMILY,
  COUNTDOWN_DEFAULT_LABEL_COLOR,
  COUNTDOWN_DEFAULT_LABEL_SIZE,
  COUNTDOWN_DEFAULT_NUMBER_COLOR,
  COUNTDOWN_DEFAULT_NUMBER_SIZE,
  COUNTDOWN_PLACEMENT,
  COUNTDOWN_SEPARATOR,
  COUNTDOWN_STYLE_PROPS,
} from '@EmailMakerReact/lib/email-maker-countdown-library.types';

export function registerMiscTypes({
  editor,
  defaultToolbarOptions,
  unstylableEmailStyles,
  dispatchDblClickOnDoubleTap,
  initRemovalOfUnsupportedCssProperties,
}: ComponentRegistrationContext): void {
  const cmpt = editor.Components;
  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.TABLE,
    model: {
      defaults: {
        trackingName: 'Divider',
        tagName: 'table',
        highlightable: true,
        droppable: false,
        draggable: true,
        hoverable: true,
        editable: true,
        selectable: true,
        deletable: true,
        badgable: true,
        name: window.i18next.t('pmwjs_divider'),
        icon: 'icon-divider',
        toolbar: defaultToolbarOptions,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        attributes: {
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER,
          'data-is-pmw-divider': 1,
          cellspacing: 0,
          cellpadding: 0,
        },
        style: {
          width: '100%',
          height: DIVIDER_DEFAULT_HEIGHT,
          border: '0',
          'border-radius': '0',
          margin: '0',
          outline: 'none',
          background: 'none',
          color: 'transparent',
          'background-color': 'transparent',
        },
        components: [
          {
            tagName: 'tr',
            type: EMAIL_MAKER_COMPONENT_TYPES.table.PMW_ROW,
            components: [
              {
                type: EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER_CELL,
                style: {
                  width: '100%',
                  height: DIVIDER_DEFAULT_HEIGHT,
                  'background-color': 'transparent',
                },
              },
            ],
          },
        ],
      },
      init(this: ComponentModelDefinition & Component) {
        // Explicit set: the addType({extend:'table'}) defaults-merge leaves this false at runtime.
        this.set(EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS, true);
        initRemovalOfUnsupportedCssProperties(this);
        const styles = this.getStyle();

        if (typeof styles.width === 'undefined') {
          styles.width = '100%';
          this.setStyle(styles);
        }

        this.setStyle({...this.defaults.style, ...this.getStyle()} as StyleProps);
      },
    },
    view: {
      events: defaultViewEvents(dispatchDblClickOnDoubleTap),
    },
    isComponent: matchByTagAndAttr('TABLE', 'data-is-pmw-divider', EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER),
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER_LEGACY, {
    model: {
      defaults: {
        trackingName: 'Divider',
        tagName: 'hr',
        highlightable: false,
        droppable: false,
        hoverable: true,
        name: window.i18next.t('pmwjs_divider'),
        badgable: true,
        toolbar: defaultToolbarOptions,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER_LEGACY,
        },
        style: {
          height: '30px',
          border: '0',
          'border-radius': '0',
          margin: '0',
        },
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
      },
    },
    view: {
      events: defaultViewEvents(dispatchDblClickOnDoubleTap),
    },
    isComponent: (el: HTMLElement) => {
      if (el.tagName && el.tagName === 'HR') {
        return {
          type: EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER_LEGACY,
        };
      }

      return false;
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIV, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.WRAPPER,
    model: {
      defaults: {
        tagName: 'div',
        trackingName: null,
        toolbar: defaultToolbarOptions,
        removable: true,
        selectable: true,
        highlightable: false,
        badgable: false,
        draggable: true,
        droppable: true,
        hoverable: true,
        stylable: true,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-is-postermywall-div': '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIV,
        },
        style: {
          padding: '0px',
          margin: '0px',
          'min-width': '10px',
        },
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
      },
    },
    isComponent: (el: HTMLElement) => {
      if (el.tagName && el.tagName === 'DIV' && !el.getAttribute('data-is-pmw-wrapper')) {
        return {
          type: EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIV,
        };
      }

      return false;
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.default.TEXT, {
    model: {
      defaults: {
        trackingName: null,
        highlightable: false,
        hoverable: false,
        selectable: false,
        badgable: false,
        droppable: false,
        draggable: false,
        copyable: false,
        editable: false,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
      },
    },
    isComponent: (el: HTMLElement) => {
      if (el instanceof Element) {
        const tagName = el.tagName.toLowerCase();

        if (RTE_TAGNAMES.includes(tagName)) {
          return {
            type: EMAIL_MAKER_COMPONENT_TYPES.default.TEXT,
          };
        }
      }

      return false;
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.default.DEFAULT, {
    model: {
      defaults: {
        trackingName: null,
        copyable: false,
        draggable: false,
        droppable: false,
        hoverable: false,
        selectable: false,
        highlightable: false,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
      },
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.default.WRAPPER, {
    model: {
      defaults: {
        trackingName: null,
        copyable: false,
        draggable: false,
        droppable: false,
        hoverable: false,
        selectable: false,
        highlightable: false,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
      },
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.default.TBODY, {
    model: {
      defaults: {
        trackingName: null,
        copyable: false,
        draggable: false,
        droppable: false,
        hoverable: false,
        selectable: false,
        highlightable: false,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        // `opacty` is a saved-template typo: the declaration never applies. Do not "fix" to `opacity` — it would change rendered output.
        style: {
          background: 'none',
          width: '100%',
          margin: '0',
          height: 'auto',
          opacty: '100%',
          border: 'none',
        },
      },
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER_CELL, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.CELL,
    model: {
      defaults: {
        tagName: 'td',
        deletable: false,
        highlightable: false,
        hoverable: false,
        selectable: false,
        badgable: false,
        droppable: false,
        draggable: false,
        copyable: false,
        editable: false,
        trackingName: null,
        toolbar: [],
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: false,
        attributes: {
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER_CELL,
          'data-is-pmw-divider-cell': 1,
          cellspacing: 0,
          cellpadding: 0,
        },
        style: {
          width: '100%',
          height: '100%',
        },
      },
    },
    view: {
      onRender(this: ComponentViewDefinition & ComponentView, {model}: {model: Component}) {
        editor.runCommand(EMAIL_MAKER_COMMANDS.REMOVE_BUGGY_BACKGROUND_ATTRIBUTE, {
          component: model,
        });
      },
    },
    isComponent: (el: HTMLElement) => {
      if (el instanceof Element && el.getAttribute('data-is-pmw-divider-cell')) {
        return {
          type: EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_DIVIDER_CELL,
        };
      }

      return false;
    },
  });

  cmpt.addType(EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_COUNTDOWN, {
    extend: EMAIL_MAKER_COMPONENT_TYPES.default.TABLE,
    model: {
      defaults: {
        tagName: 'table',
        trackingName: 'Countdown',
        name: window.i18next.t('pmwjs_countdown'),
        icon: 'icon-timer',
        toolbar: defaultToolbarOptions,
        removable: true,
        selectable: true,
        draggable: true,
        droppable: false,
        editable: false,
        hoverable: true,
        highlightable: true,
        badgable: true,
        stylable: true,
        unstylable: unstylableEmailStyles,
        [EMAIL_MAKER_PROPS.HAS_BORDER_CONTROLS]: true,
        attributes: {
          [COUNTDOWN_ATTRIBUTES.IS_COUNTDOWN]: '1',
          'data-pmw-type': EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_COUNTDOWN,
          role: 'presentation',
          align: 'center',
          cellspacing: 0,
          cellpadding: 0,
        },
        style: {
          width: 'auto',
          margin: '0 auto',
          'border-collapse': 'separate',
          'border-radius': '12px',
          'font-family': COUNTDOWN_DEFAULT_FONT_FAMILY,
          'font-size': COUNTDOWN_DEFAULT_NUMBER_SIZE,
          'font-weight': 'bold',
          color: COUNTDOWN_DEFAULT_NUMBER_COLOR,
          [COUNTDOWN_STYLE_PROPS.SEPARATOR]: COUNTDOWN_SEPARATOR.COLON,
          [COUNTDOWN_STYLE_PROPS.PLACEMENT]: COUNTDOWN_PLACEMENT.SPREAD,
          [COUNTDOWN_STYLE_PROPS.LABEL_SIZE]: COUNTDOWN_DEFAULT_LABEL_SIZE,
          [COUNTDOWN_STYLE_PROPS.LABEL_COLOR]: COUNTDOWN_DEFAULT_LABEL_COLOR,
        },
        components: [],
      },
      init(this: ComponentModelDefinition & Component) {
        initRemovalOfUnsupportedCssProperties(this);
        setCountdownDefaults(this);
        refreshCountdown(editor, this);
      },
    },
    view: {
      events: defaultViewEvents(dispatchDblClickOnDoubleTap),
    },
    isComponent: matchByTagAndAttr('TABLE', COUNTDOWN_ATTRIBUTES.IS_COUNTDOWN, EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_COUNTDOWN),
  });

  editor.DomComponents.addType('script', {
    isComponent: () => false,
  });

  editor.DomComponents.addType('iframe', {
    isComponent: () => false,
  });
}
